import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { environment } from 'src/environments/environment';

export interface AnalyticsEvent {
  type: string;         // e.g. 'widget_interaction', 'setting_change', 'permission_status'
  timestamp: number;    // epoch ms
  [key: string]: any;   // flexible additional properties
}

interface AnalyticsPayload {
  userId: string;
  sessionId: string;
  appVersion: string;
  appOS: string;
  osVersion: string;
  events: AnalyticsEvent[];
}

const PENDING_EVENTS_KEY = 'analytics_pending_events';
const FLUSH_INTERVAL_MS = 30_000;
const BATCH_SIZE_LIMIT = 20;

@Injectable({
  providedIn: 'root'
})
export class EventPipelineService implements OnDestroy {

  private sessionId: string;
  private userId: string | null = null;
  private appVersion: string | null = null;
  private appOS: string | null = null;
  private osVersion: string | null = null;

  private queue: AnalyticsEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private http: HttpClient,
    private platform: Platform
  ) {
    this.sessionId = this.generateUUID();
    this.startFlushTimer();
    this.registerAppStateListener();
    this.loadPendingEventsAndFlush();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  setUserContext(userId: string, appVersion: string, appOS: string, osVersion: string): void {
    this.userId = userId;
    this.appVersion = appVersion;
    this.appOS = appOS;
    this.osVersion = osVersion;
  }

  clearUserContext(): void {
    this.userId = null;
    this.appVersion = null;
    this.appOS = null;
    this.osVersion = null;
  }

  trackEvent(event: AnalyticsEvent): void {
    try {
      this.queue.push(event);
      if (this.queue.length >= BATCH_SIZE_LIMIT) {
        this.flush();
      }
    } catch {
      // Never break the app
    }
  }

  async flush(): Promise<void> {
    try {
      // Merge any previously persisted events first
      const persisted = await this.loadPersistedEvents();
      if (persisted.length > 0) {
        this.queue = [...persisted, ...this.queue];
        await this.clearPersistedEvents();
      }

      if (this.queue.length === 0) {
        return;
      }

      // Snapshot & clear the in-memory queue before the async call
      const batch = [...this.queue];
      this.queue = [];

      await this.sendBatch(batch);
    } catch {
      // Never break the app
    }
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnDestroy(): void {
    this.stopFlushTimer();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private async sendBatch(events: AnalyticsEvent[]): Promise<void> {
    if (!environment.analyticsEventsUrl) {
      return;
    }

    const payload: AnalyticsPayload = {
      userId: this.userId ?? 'anonymous',
      sessionId: this.sessionId,
      appVersion: this.appVersion ?? environment.appVersion,
      appOS: this.appOS ?? 'unknown',
      osVersion: this.osVersion ?? 'unknown',
      events
    };

    try {
      await this.http.post(environment.analyticsEventsUrl, payload).toPromise();
    } catch {
      // HTTP failure — persist for next cycle
      try {
        await this.persistEvents(events);
      } catch {
        // Still never throw
      }
    }
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  private stopFlushTimer(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private registerAppStateListener(): void {
    try {
      if (this.platform.is('capacitor')) {
        App.addListener('appStateChange', (state) => {
          if (!state.isActive) {
            // App going to background — flush immediately
            this.flush();
          } else {
            // App resuming — load persisted events and flush
            this.loadPendingEventsAndFlush();
          }
        });
      }
    } catch {
      // Never break the app
    }
  }

  private async loadPendingEventsAndFlush(): Promise<void> {
    try {
      const persisted = await this.loadPersistedEvents();
      if (persisted.length > 0) {
        this.queue = [...persisted, ...this.queue];
        await this.clearPersistedEvents();
        await this.flush();
      }
    } catch {
      // Never break the app
    }
  }

  private async persistEvents(events: AnalyticsEvent[]): Promise<void> {
    try {
      const existing = await this.loadPersistedEvents();
      const merged = [...existing, ...events];
      await Preferences.set({
        key: PENDING_EVENTS_KEY,
        value: JSON.stringify(merged)
      });
    } catch {
      // Never break the app
    }
  }

  private async loadPersistedEvents(): Promise<AnalyticsEvent[]> {
    try {
      const { value } = await Preferences.get({ key: PENDING_EVENTS_KEY });
      if (value) {
        return JSON.parse(value) as AnalyticsEvent[];
      }
    } catch {
      // Corrupt data — ignore
    }
    return [];
  }

  private async clearPersistedEvents(): Promise<void> {
    try {
      await Preferences.remove({ key: PENDING_EVENTS_KEY });
    } catch {
      // Never break the app
    }
  }

  private generateUUID(): string {
    try {
      return crypto.randomUUID();
    } catch {
      // Fallback for environments where crypto.randomUUID is unavailable
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }
  }
}
